import { inject, Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';

import {
  AuthEvent,
  AuthTelemetryReporter,
  BIOMETRIC_SIGN_IN,
  biometricCause,
  errorDetail,
  VAULT,
} from '@ninety/mobile/platform/auth';

import { CommandRunner } from './command-runner.service';
import { PendingCredentials } from './pending-credentials.service';

/**
 * A throwaway key, used to find out whether this device can store anything at all.
 *
 * Deliberately outside `DURABLE_KEY_PATTERN`, so it can never be mistaken for part of a session:
 * `hasSession()` asks for a specific set of keys, and a probe that matched the pattern would be a
 * key the launch had to reason about.
 */
const STORAGE_PROBE_KEY = 'ninety.auth.probe';

/**
 * The handlers for every Command that touches biometric sign-in.
 *
 * Separate from `SessionVaultEffects` because they are separate subsystems with separate lifetimes:
 * one owns the Durable Root, which a sign-out destroys, and this one owns a stored password, which a
 * sign-out deliberately keeps. Nothing here reads or writes the Vault except the launch probe.
 */
@Injectable()
export class SessionBiometricsEffects {
  private readonly commands = inject(CommandRunner);
  private readonly biometrics = inject(BIOMETRIC_SIGN_IN);
  private readonly vault = inject(VAULT);
  private readonly pending = inject(PendingCredentials);
  private readonly reporter = inject(AuthTelemetryReporter);

  /**
   * The one fact the launch needs.
   *
   * It asked two under the App Lock — whether storage worked, and whether a prompt stood in front of
   * it. There is no prompt now, so there is one question and no `Promise.all`.
   */
  readonly readLaunchState$ = createEffect(() =>
    this.commands.on('readLaunchState', async (): Promise<AuthEvent> => {
      return { type: 'launchStateRead', storageUsable: await this.storageUsable() };
    })
  );

  /**
   * Prompt, read the keychain, and hand the credentials to the machine.
   *
   * Both outcomes are events rather than escapes, because the sign-in screen is behind a spinner until
   * one of them lands. A refusal and an unreadable item are the same event on purpose — see
   * `biometricSignInFailed` — and neither deletes anything.
   */
  readonly biometricSignIn$ = createEffect(() =>
    this.commands.on('biometricSignIn', async (): Promise<AuthEvent> => {
      try {
        const { email, password } = await this.biometrics.signIn();
        return { type: 'storedCredentialsRead', email, password };
      } catch (error) {
        return { type: 'biometricSignInFailed', cause: biometricCause(error) };
      }
    })
  );

  /**
   * Store the credentials, or delete them.
   *
   * **This is the only thing that writes a password to the device, and it runs on the tap that
   * consents to it.** What it enrolls is whatever {@link PendingCredentials} is holding.
   *
   * Neither direction prompts — `main` verifies nothing on either, and there is nothing left to prove:
   * enabling happens moments after a password sign-in, and the worst outcome of an unasked-for disable
   * is that someone types a password. The App Lock's "disabling must be authorized" property went with
   * the lock it was protecting.
   *
   * **Neither direction carries a password**, and that is what keeps plaintext off every event, out of
   * the reducer, and out of `describeState`. The Command says only `enabled`; the pair is fetched here
   * from the holder rather than handed down through the machine.
   *
   * A failure is reported as `taskFailed` rather than left to escape, because the machine has a
   * decision to make about it: the task returns to `authorized{idle}` either way, and anything that
   * reached `commandFailed` instead would leave the control that started it spinning for the rest of
   * the session.
   */
  readonly enrollBiometrics$ = createEffect(() =>
    this.commands.on('enrollBiometrics', async ({ enabled }): Promise<AuthEvent> => {
      try {
        if (!enabled) {
          await this.biometrics.disable();
          return { type: 'biometricEnrollmentFinished' };
        }

        // Taken rather than read, so the answer ends the password's life here whichever way the write
        // goes. Refused rather than recorded when there is nothing: an enrollment with no credentials
        // behind it is a button that raises a prompt and then always fails. The offer gates on the same
        // holder, so this should be unreachable — which is why it is worth an outcome rather than an
        // assumption.
        const credentials = this.pending.take();
        if (!credentials) return { type: 'taskFailed', cause: { kind: 'vaultUnavailable' } };

        await this.saveUnlessEnrolled(credentials.email, credentials.password);
        await this.biometrics.enroll();
        return { type: 'biometricEnrollmentFinished' };
      } catch (error) {
        return { type: 'taskFailed', cause: biometricCause(error) };
      }
    })
  );

  /**
   * Delete the stored credentials and tell the machine nothing.
   *
   * Teardown, like `clearVault`: it is issued alongside a landing the machine has already decided on —
   * a refused replay, or a completed password reset — so there is no outcome to report and no wait to
   * end. A delete that fails is still worth an `auth.command_failed`, which is what `teardown` does.
   *
   * The same port call the Settings control makes, because it is the same write: delete the item and
   * record that nothing is enrolled. Two Commands rather than one because the machine treats them
   * differently — this one is teardown, and Settings' is a task with a screen waiting on it — but there
   * is only one thing to do. See `BiometricSignIn.disable`.
   */
  readonly forgetCredentials$ = createEffect(() =>
    this.commands.teardown('forgetCredentials', () => this.biometrics.disable())
  );

  /**
   * Write the credentials, unless this device already has an enrollment.
   *
   * **Never over an existing enrollment**, which is the one case this must refuse. The keychain holds
   * one item for the device, and the consent guarding it is a bare preference with no account attached.
   * So without this, accepting the offer on a device that already has an enrollment would replace the
   * credential behind it — which may well be for a different account — under a preference that already
   * said yes to something else.
   *
   * The cost is a stored copy that is not refreshed by a later sign-in from the enrolled person
   * themselves. If they changed their password elsewhere, the copy here is stale — and that is already
   * handled, once, by the rule that forgets it: see `failGrant` and `authorizing.source`.
   *
   * **A failure is reported as well as thrown.** Thrown, because the person is watching and the sheet
   * must not close on a write that did not happen; reported, because `taskFailed` describes the tap
   * rather than the store — see `credentialStoreFailed`, and `errorDetail` for why the reason is read
   * off two named fields rather than serialised.
   */
  private async saveUnlessEnrolled(email: string, password: string): Promise<void> {
    if ((await this.biometrics.enrollment()) === 'enrolled') return;
    try {
      await this.biometrics.saveCredentials(email, password);
    } catch (error) {
      this.reporter.credentialStoreFailed('save', errorDetail(error));
      throw error;
    }
  }

  /**
   * Whether this device can hold a session at all.
   *
   * A write-then-remove of a throwaway key, because that is the only question with a usable answer:
   * `Vault.keys()` answers `[]` both for an empty store and for one it could not list, and those
   * route to opposite places. A launch on a device that cannot store anything must reach
   * `noSession`, which leaves the Vault alone, rather than `vaultUnavailable`, which clears it.
   *
   * A change to either has to account for the other.
   */
  private async storageUsable(): Promise<boolean> {
    try {
      await this.vault.write(STORAGE_PROBE_KEY, 'probe');
      await this.vault.remove(STORAGE_PROBE_KEY);
      return true;
    } catch {
      return false;
    }
  }
}
